import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Coins, ChevronLeft, ChevronRight, RefreshCw, Loader2, TrendingUp, Receipt,
  PackageMinus, AlertTriangle, ChevronDown, Percent,
} from 'lucide-react';

const eur = (v) => (v === null || v === undefined ? '—' : new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v));
const num = (v) => new Intl.NumberFormat('pt-PT', { maximumFractionDigits: 0 }).format(v || 0);
const pct = (v) => (v === null || v === undefined ? '—' : `${v.toFixed(1)}%`);
const DOC_TYPES = ['ISL', 'IEL', 'IQU', 'CPR'];
const DOC_LABEL = { ISL: 'Saídas (ISL)', IEL: 'Entradas (IEL)', IQU: 'Quebras (IQU)', CPR: 'Consumos (CPR)' };
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const shiftMonth = (m, delta) => {
  const [y, mo] = m.split('-').map(Number);
  const d = new Date(y, mo - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const monthLabel = (m) => {
  const [y, mo] = m.split('-').map(Number);
  const label = new Date(y, mo - 1, 1).toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const Kpi = ({ title, value, sub, icon: Icon, tone = 'slate', testId }) => (
  <Card data-testid={testId}>
    <CardContent className="pt-4 pb-3 flex items-start justify-between">
      <div>
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{title}</p>
        <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
        {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
      </div>
      <div className={`p-2 rounded-lg bg-${tone}-50 text-${tone}-600`}><Icon className="w-5 h-5" /></div>
    </CardContent>
  </Card>
);

const DocCell = ({ t }) => (
  <td className="px-3 py-2 text-right whitespace-nowrap">
    <div className="font-medium">{eur(t.value)}</div>
    <div className="text-[11px] text-slate-400">{t.count} doc · {num(t.units)} un</div>
  </td>
);

const DocumentsList = ({ docs }) => (
  <div className="bg-slate-50 px-4 py-3">
    <table className="w-full text-xs">
      <thead className="text-slate-500">
        <tr><th className="text-left py-1">Documento</th><th className="text-left">Data</th><th className="text-left">Sector</th>
          <th className="text-left">V/ número</th><th className="text-right">Unid.</th><th className="text-right">Valor</th></tr>
      </thead>
      <tbody>
        {docs.map((d) => (
          <tr key={d.key} className="border-t border-slate-200" data-testid={`cost-doc-${d.key}`}>
            <td className="py-1 font-mono">{d.reference}</td>
            <td>{d.date}</td>
            <td>{d.sector_ok ? d.sector : <Badge className="bg-red-100 text-red-700 border border-red-200">{d.sector} ≠ {d.warehouse}</Badge>}</td>
            <td className="font-mono text-slate-500">{d.vosso_numero || '—'}</td>
            <td className="text-right">{num(d.units)}</td>
            <td className="text-right">{eur(d.value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const StoreRow = ({ s, open, onToggle }) => (
  <>
    <tr className="border-t hover:bg-slate-50 cursor-pointer" onClick={onToggle} data-testid={`cost-store-row-${s.warehouse}`}>
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? '' : '-rotate-90'}`} />
          <div>
            <div className="font-medium text-slate-900">{s.name}</div>
            <div className="text-[11px] text-slate-400">Armazém {s.warehouse}</div>
          </div>
        </div>
      </td>
      <td className="px-3 py-2 text-right font-medium">{eur(s.sales.net_sales_ex_vat)}</td>
      {DOC_TYPES.map((t) => <DocCell key={t} t={s.totals[t]} />)}
      <td className="px-3 py-2 text-right">{eur(s.cost_of_sales)}</td>
      <td className={`px-3 py-2 text-right font-semibold ${s.gross_margin < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
        {eur(s.gross_margin)}<div className="text-[11px] font-normal">{pct(s.gross_margin_pct)}</div>
      </td>
      <td className="px-3 py-2 text-right">{eur(s.margin_after_breakage)}</td>
      <td className="px-3 py-2 text-center">
        {s.wrong_sector_count > 0
          ? <Badge className="bg-red-100 text-red-700 border border-red-200">{s.wrong_sector_count}</Badge>
          : <span className="text-slate-300">0</span>}
      </td>
    </tr>
    {open && (
      <tr><td colSpan={10} className="p-0">{s.documents.length ? <DocumentsList docs={s.documents} /> : <div className="px-4 py-3 text-xs text-slate-500">Sem documentos SAGE neste mês.</div>}</td></tr>
    )}
  </>
);

const StoresTable = ({ stores }) => {
  const [open, setOpen] = useState(null);
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Por loja</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto p-0">
        <table className="w-full text-sm" data-testid="costs-stores-table">
          <thead className="bg-slate-50 text-slate-600 text-xs">
            <tr>
              <th className="px-3 py-2 text-left">Loja</th><th className="px-3 py-2 text-right">Vendas s/IVA</th>
              {DOC_TYPES.map((t) => <th key={t} className="px-3 py-2 text-right">{DOC_LABEL[t]}</th>)}
              <th className="px-3 py-2 text-right">Custo vendas</th><th className="px-3 py-2 text-right">Margem bruta</th>
              <th className="px-3 py-2 text-right">Após quebras</th><th className="px-3 py-2 text-center">Sector errado</th>
            </tr>
          </thead>
          <tbody>
            {stores.map((s) => <StoreRow key={s.warehouse} s={s} open={open === s.warehouse} onToggle={() => setOpen(open === s.warehouse ? null : s.warehouse)} />)}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};

const SectorsTable = ({ sectors }) => (
  <Card>
    <CardHeader className="pb-2"><CardTitle className="text-base">Por sector SAGE</CardTitle></CardHeader>
    <CardContent className="overflow-x-auto p-0">
      <table className="w-full text-sm" data-testid="costs-sectors-table">
        <thead className="bg-slate-50 text-slate-600 text-xs">
          <tr><th className="px-3 py-2 text-left">Sector</th>{DOC_TYPES.map((t) => <th key={t} className="px-3 py-2 text-right">{DOC_LABEL[t]}</th>)}</tr>
        </thead>
        <tbody>
          {sectors.map((s) => (
            <tr key={s.sector} className="border-t" data-testid={`cost-sector-row-${s.sector}`}>
              <td className="px-3 py-2 font-medium">{s.sector}</td>
              {DOC_TYPES.map((t) => <DocCell key={t} t={s.totals[t]} />)}
            </tr>
          ))}
        </tbody>
      </table>
    </CardContent>
  </Card>
);

export default function RetailCustos() {
  const [month, setMonth] = useState(() => shiftMonth(new Date().toISOString().slice(0, 7), 0));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/retail-costs/summary`, { params: { month }, ...auth() });
      setData(res.data);
    } catch (e) {
      toast.error('Erro ao carregar custos');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/retail-costs/sync`, null, { params: { month, full: true }, ...auth() });
      const total = Object.values(res.data.stats).reduce((a, s) => a + s.new + s.known, 0);
      toast.success(`Sincronizado com o SAGE: ${total} documentos lidos`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao sincronizar com o SAGE');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-7xl" data-testid="retail-costs-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2"><Coins className="w-6 h-6" /> Custos das Lojas</h1>
          <p className="text-sm text-slate-500">Saídas, entradas, quebras e consumos no SAGE vs vendas WinTouch · margem bruta mensal</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="costs-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="w-40 text-center font-medium" data-testid="costs-month-label">{monthLabel(month)}</span>
          <Button variant="outline" size="icon" onClick={() => setMonth(shiftMonth(month, 1))} data-testid="costs-next-month"><ChevronRight className="w-4 h-4" /></Button>
          <Button onClick={sync} disabled={syncing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="costs-sync-btn">
            {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Sincronizar com SAGE
          </Button>
        </div>
      </div>
      <p className="text-xs text-slate-400 -mt-4" data-testid="costs-last-sync">
        Valores ao custo gravado em cada documento SAGE (documentos anulados excluídos). Última sincronização: {data?.last_sync_at ? new Date(data.last_sync_at).toLocaleString('pt-PT') : 'nunca'}
      </p>

      {loading && !data ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Kpi title="Vendas s/IVA" value={eur(data.net_sales_ex_vat)} sub="vendas − devoluções (WinTouch)" icon={Receipt} tone="blue" testId="kpi-sales" />
            <Kpi title="Custo das vendas" value={eur(data.cost_of_sales)} sub="ISL − IEL" icon={PackageMinus} tone="amber" testId="kpi-cogs" />
            <Kpi title="Margem bruta" value={eur(data.gross_margin)} sub={`${pct(data.gross_margin_pct)} das vendas`} icon={TrendingUp} tone="emerald" testId="kpi-margin" />
            <Kpi title="Quebras + Consumos" value={eur(data.breakage_consumption)} sub="IQU + CPR" icon={AlertTriangle} tone="red" testId="kpi-breakage" />
            <Kpi title="Margem após quebras" value={eur(data.margin_after_breakage)} sub="margem bruta − IQU − CPR" icon={Percent} tone="slate" testId="kpi-margin-after" />
          </div>

          {data.wrong_sector.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 flex gap-3" data-testid="costs-wrong-sector-alert">
              <AlertTriangle className="w-5 h-5 shrink-0" />
              <div>
                <strong>{data.wrong_sector.length} documentos</strong> deste mês estão num sector diferente do armazém da loja (ex.: 001 Sede).
                Abra cada loja na tabela para ver quais. Os totais por loja já os contam no armazém certo; a tabela por sector mostra como estão hoje no SAGE.
              </div>
            </div>
          )}

          <StoresTable stores={data.stores} />
          <SectorsTable sectors={data.sectors} />
        </>
      )}
    </div>
  );
}
